<template>
    <div class="form-group d-flex flex-column dashboard-select-dropdown">
        <label
            v-if="label"
            class="col-form-label d-flex justify-content-between"
        >
            {{ label }}{{ required ? '*' : '' }}
        </label>
        <div
            ref="trigger"
            class="form-control d-flex"
            :class="{
                'input-invalid': isInvalid && userInteracted,
                disabled: disabled,
            }"
            :tabindex="disabled ? '-1' : '0'"
            @click="handleTriggerClick"
            @keydown="handleTriggerKeydown"
        >
            <div class="form-control-content">
                <span
                    v-if="!getSelectedCount"
                    :style="{
                        color: showDropdown
                            ? 'var(--black)'
                            : 'var(--neutral-800)',
                    }"
                >
                    {{ placeholderText }}
                </span>
                <div v-else class="selected-badges">
                    <SelectedBadge
                        v-for="(item, index) in selectedItems"
                        :key="index"
                        :text="getItemDisplayText(item)"
                        :plain-text="plainTextMode"
                    />
                </div>
            </div>
            <div class="actions">
                <i
                    class="fi fi-chevron-down"
                    :class="{ rotated: showDropdown, disabled: disabled }"
                ></i>
            </div>
        </div>
        <!-- Hidden input for form validation -->
        <input
            ref="hiddenInput"
            type="text"
            v-if="required"
            :required="required"
            tabindex="-1"
            aria-hidden="true"
            style="
                opacity: 0;
                position: absolute;
                z-index: -1;
                width: 0;
                height: 0;
            "
            :value="getSelectedCount ? 'selected' : ''"
            @invalid.prevent="handleInvalid"
        />
        <div
            v-if="isInvalid && userInteracted"
            class="font-note"
            style="color: var(--danger)"
        >
            {{ errorMessage }}
        </div>
    </div>

    <Teleport to="body">
        <MultiselectDropdown
            :visible="showDropdown"
            :dropdown-position="dropdownPosition"
            :dropdown-direction="dropdownDirection"
            :options="options"
            :model-value="selectedValues"
            :selection-type="type"
            :search-placeholder="searchPlaceholder"
            :selected-items="selectedItems"
            :selection-order="selectedOrder"
            :selected-count="getSelectedCount"
            :required="required"
            :disabled="disabled"
            :error-message="errorMessage"
            :is-invalid="isInvalid"
            :user-interacted="userInteracted"
            :allow-create="allowCreate"
            @update:model-value="updateSelectedValues"
            @remove-selection="removeSelection"
            @clear-selection="clearSelection"
            @close-dropdown="closeDropdown"
            @validate="handleValidation"
            @create-option="handleCreateOption"
            @remove-option="handleRemoveOption"
        />
    </Teleport>
</template>

<script>
import SelectedBadge from './SelectedBadge.vue';
import MultiselectDropdown from './MultiselectDropdown.vue';

/**
 * @component MultiselectItem
 * @description Multiselect component with dropdown for selecting options
 */
export default {
    name: 'MultiselectItem',
    components: {
        SelectedBadge,
        MultiselectDropdown,
    },
    props: {
        /** Label text displayed above the field */
        label: {
            type: String,
            required: true,
        },
        /** Array of options for the multiselect */
        options: {
            type: Array,
            required: true,
        },
        /** Selected values object */
        modelValue: {
            type: Object,
            default: () => ({}),
        },
        /** Selection mode (multiselect or single-select) */
        type: {
            type: String,
            default: 'multiselect',
            validator: (value) =>
                ['multiselect', 'single-select'].includes(value),
        },
        /** Placeholder text when no options selected */
        placeholderText: {
            type: String,
            default: 'Select any option',
        },
        /** Placeholder text for search input */
        searchPlaceholder: {
            type: String,
            default: '',
        },
        /** Whether field is required for form validation */
        required: {
            type: Boolean,
            default: false,
        },
        /** Whether field is disabled */
        disabled: {
            type: Boolean,
            default: false,
        },
        /** Error message for validation failures */
        errorMessage: {
            type: String,
            default: 'Please select an option',
        },

        /** Whether to allow creating new options */
        allowCreate: {
            type: Boolean,
            default: false,
        },
        /** Whether to render selected badges as plain text */
        plainTextMode: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            selectedValues: {}, // Local copy of selected values
            selectedOrder: [], // Track selection order
            showDropdown: false, // Control dropdown visibility
            isInvalid: false, // Track validation state
            userInteracted: false, // Track if user interacted with component
            dropdownPosition: {
                top: '0px',
                left: '0px',
                width: '0px',
                maxHeight: '400px',
            }, // Position for dropdown
            positionWatcher: null, // Animation frame for position updates
            lastTriggerRect: null, // Last trigger element position
            formSubmitted: false, // Track if form was submitted
            initialValidationComplete: false, // Track initial validation
            formElement: null, // Reference to parent form
        };
    },
    emits: [
        'create-option',
        'update:modelValue',
        'remove-option',
        'clear-selection',
        'close-dropdown',
        'validate',
        'dropdown-toggle',
    ],
    computed: {
        /** Number of selected options */
        getSelectedCount() {
            return Object.values(this.selectedValues).filter(Boolean).length;
        },

        /** Selected options in selection order */
        selectedItems() {
            if (this.selectedOrder.length === 0) {
                return this.options.filter((opt) => {
                    const value = this.getOptionValue(opt);
                    return this.selectedValues[value] || opt.default;
                });
            }

            return this.selectedOrder
                .map((value) =>
                    this.options.find(
                        (opt) => this.getOptionValue(opt) == value
                    )
                )
                .filter(Boolean);
        },

        /** Direction for dropdown animation */
        dropdownDirection() {
            const triggerRect = this.lastTriggerRect;
            if (!triggerRect) return 'dropdown-stretch-down';

            const viewportHeight = window.innerHeight;
            const spaceBelow = viewportHeight - triggerRect.bottom;

            return spaceBelow >= 200 || spaceBelow > triggerRect.top
                ? 'dropdown-stretch-down'
                : 'dropdown-stretch-up';
        },
    },
    watch: {
        /** Handle model value changes */
        modelValue: {
            immediate: true,
            deep: true,
            handler(newVal) {
                if (this.disabled) {
                    // If disabled, just update internal state without emitting
                    this.selectedValues = { ...newVal };
                    // Update order based on the new disabled value
                    this.selectedOrder = Object.entries(this.selectedValues)
                        .filter(([_, isSelected]) => isSelected)
                        .map(([key]) => key);
                    return;
                }

                // Directly update selectedValues based on the prop
                this.selectedValues = { ...newVal };

                // Update selectedOrder based on the new values
                this.selectedOrder = Object.entries(this.selectedValues)
                    .filter(([_, isSelected]) => isSelected)
                    .map(([key]) => key);
            },
        },
        /** Close dropdown when component becomes disabled */
        disabled(newVal) {
            if (newVal && this.showDropdown) {
                this.closeDropdown();
            }
        },
        /** Reset invalid state when selection changes */
        getSelectedCount(newVal) {
            if (newVal > 0 && this.isInvalid) {
                this.isInvalid = false;
            }
        },
        /** Trigger validation when form is submitted */
        formSubmitted(newVal) {
            if (newVal) {
                this.validateField();
            }
        },
    },
    methods: {
        /** Gets value from option */
        getOptionValue(option) {
            return option?.value || option;
        },

        /** Gets display text for item */
        getItemDisplayText(item) {
            return item?.text || item?.label || item;
        },

        /** Handles click on trigger element */
        handleTriggerClick(event) {
            if (!this.disabled) {
                this.userInteracted = true;
                this.openDropdown();
            }
        },

        handleTriggerKeydown(event) {
            if (this.disabled) return;
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                this.openDropdown();
            }
        },

        /** Opens the dropdown */
        openDropdown() {
            if (this.disabled) return;

            if (this.showDropdown) {
                this.closeDropdown();
            } else {
                this.lastTriggerRect =
                    this.$refs.trigger.getBoundingClientRect();
                this.calculatePosition();

                this.showDropdown = true;
                this.$emit('dropdown-toggle', true);

                this.$nextTick(() => {
                    this.startPositionWatcher();
                });
            }
        },

        /** Handles invalid event from hidden input */
        handleInvalid() {
            this.isInvalid = true;
            this.formSubmitted = true;
            this.userInteracted = true;
            this.$emit('validate', false);
        },

        /** Validates the field */
        validateField() {
            if (this.disabled || !this.initialValidationComplete) return true;

            if (this.required) {
                const hasSelection = Object.values(this.selectedValues).some(
                    (value) => value === true
                );

                if (this.formSubmitted) {
                    this.isInvalid = !hasSelection;
                    this.userInteracted = true;
                } else if (this.userInteracted) {
                    this.isInvalid = !hasSelection;
                }

                this.$emit('validate', hasSelection);
                return hasSelection;
            }

            this.isInvalid = false;
            this.$emit('validate', true);
            return true;
        },

        /** Explicitly triggers validation */
        triggerValidation() {
            this.formSubmitted = true;
            this.userInteracted = true;

            if (this.required && !this.getSelectedCount) {
                this.isInvalid = true;
                this.$emit('validate', false);
                return false;
            }

            return this.validateField();
        },

        /** Handles validation from dropdown */
        handleValidation(isValid) {
            if (this.disabled) return;

            this.isInvalid = !isValid;
            this.$emit('validate', isValid);
        },

        /** Handles creating a new option */
        handleCreateOption(newOptionValue) {
            this.$emit('create-option', newOptionValue);
        },

        /** Handles option removal for form template builder */
        handleRemoveOption(optionToRemove) {
            this.$emit('remove-option', optionToRemove);
        },

        /** Calculates dropdown position */
        calculateDropdownPosition() {
            const triggerRect = this.lastTriggerRect;
            if (!triggerRect) return { top: '0px', left: '0px', width: '0px' };

            const viewportHeight = window.innerHeight;
            const viewportWidth = window.innerWidth;

            const spaceAbove = triggerRect.top;
            const spaceBelow = viewportHeight - triggerRect.bottom;

            const maxHeightBelow = Math.min(400, spaceBelow - 20);
            const maxHeightAbove = Math.min(300, spaceAbove - 20);

            const left = Math.max(0, triggerRect.left);
            const width = Math.min(triggerRect.width, viewportWidth - left);

            if (spaceBelow >= 200 || spaceBelow > spaceAbove) {
                return {
                    top: `${triggerRect.bottom + 8}px`,
                    left: `${left}px`,
                    width: `${width}px`,
                    maxHeight: `${maxHeightBelow}px`,
                };
            } else {
                const windowHeight = window.innerHeight;
                const bottomPosition = windowHeight - triggerRect.top + 8;

                return {
                    bottom: `${bottomPosition}px`,
                    left: `${left}px`,
                    width: `${width}px`,
                    maxHeight: `${maxHeightAbove}px`,
                };
            }
        },

        /** Calculates position of dropdown */
        calculatePosition() {
            this.dropdownPosition = this.calculateDropdownPosition();
        },

        /** Starts watching for position changes */
        startPositionWatcher() {
            const checkPosition = () => {
                if (!this.showDropdown) return;

                const currentRect = this.$refs.trigger.getBoundingClientRect();
                const changed = [
                    currentRect.top !== this.lastTriggerRect.top,
                    currentRect.bottom !== this.lastTriggerRect.bottom,
                    currentRect.left !== this.lastTriggerRect.left,
                    currentRect.right !== this.lastTriggerRect.right,
                ].some(Boolean);

                if (changed) {
                    this.lastTriggerRect = currentRect;
                    this.calculatePosition();
                }

                this.positionWatcher = requestAnimationFrame(checkPosition);
            };

            this.positionWatcher = requestAnimationFrame(checkPosition);
        },

        /** Closes the dropdown */
        closeDropdown() {
            this.showDropdown = false;
            this.$emit('dropdown-toggle', false);

            if (this.positionWatcher) {
                cancelAnimationFrame(this.positionWatcher);
                this.positionWatcher = null;
            }

            this.$nextTick(() => {
                if (this.$refs.trigger) {
                    this.$refs.trigger.focus();
                }
            });
        },

        /** Handles clicks outside the component */
        handleClickOutside(event) {
            if (!this.showDropdown) return;

            const isOutsideTrigger = !this.$refs.trigger.contains(event.target);

            const dropdownElement = document.querySelector(
                '.selection-dropdown'
            );
            const isOutsideDropdown =
                !dropdownElement || !dropdownElement.contains(event.target);

            if (isOutsideDropdown && isOutsideTrigger) {
                this.closeDropdown();
                event.stopPropagation();
            }
        },

        /** Updates selected values */
        updateSelectedValues(values, order = null) {
            this.userInteracted = true;
            this.selectedValues = values;

            if (order) {
                this.selectedOrder = [...order];
            } else {
                this.selectedOrder = Object.entries(values)
                    .filter(([_, isSelected]) => isSelected)
                    .map(([value]) => value);
            }

            this.$emit('update:modelValue', this.selectedValues);
        },

        /** Clears all selections */
        clearSelection() {
            this.userInteracted = true;
            this.selectedValues = {};
            this.selectedOrder = [];
            this.options.forEach((opt) => {
                const key = this.getOptionValue(opt);
                this.selectedValues[key] = false;
            });
            this.$emit('update:modelValue', this.selectedValues);
        },

        /** Removes a selected option */
        removeSelection(value) {
            this.userInteracted = true;
            this.selectedValues[value] = false;
            this.selectedOrder = this.selectedOrder.filter((v) => v !== value);
            this.$emit('update:modelValue', this.selectedValues);
        },

        /** Finds closest form element and attaches submit listener */
        findAndAttachToForm() {
            let element = this.$el;
            while (element && element.tagName !== 'FORM') {
                element = element.parentElement;
            }

            if (element) {
                this.formElement = element;
                this.formElement.addEventListener(
                    'submit',
                    this.handleFormSubmit
                );
            }
        },

        /** Removes submit listener from form element */
        detachFromForm() {
            if (this.formElement) {
                this.formElement.removeEventListener(
                    'submit',
                    this.handleFormSubmit
                );
                this.formElement = null;
            }
        },

        /** Handles form submission from parent form */
        handleFormSubmit(event) {
            this.formSubmitted = true;
            this.userInteracted = true;

            const isValid = this.validateField();

            if (this.required && !isValid) {
                event.preventDefault();

                if (this.$refs.hiddenInput) {
                    this.$refs.hiddenInput.reportValidity();
                }
            }
        },
    },
    mounted() {
        document.addEventListener('click', this.handleClickOutside);

        this.$nextTick(() => {
            this.initialValidationComplete = true;
        });

        this.findAndAttachToForm();
    },
    beforeUnmount() {
        document.removeEventListener('click', this.handleClickOutside);
        if (this.positionWatcher) {
            cancelAnimationFrame(this.positionWatcher);
        }

        this.detachFromForm();
    },
};
</script>

<style scoped>
.form-group.flex-column {
    gap: var(--space-2);
}

.form-control {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: var(--size-10);
    max-height: 100%;
    transition: border-color 0.2s ease;
    padding: var(--space-1) var(--space-3);
}

.form-control.disabled {
    cursor: not-allowed;
    background-color: var(--neutral-500);
    border: 1px solid var(--neutral-500);
}

.form-control.input-invalid {
    border: 1px solid var(--danger);
}

.form-control-content {
    flex: 1;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.selected-badges {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-1);
}

.actions {
    display: flex;
    align-items: center;
    margin-left: var(--space-2);
}

.fi-chevron-down {
    transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    font-size: 0.75rem;
}

.fi-chevron-down.rotated {
    transform: rotate(180deg);
}

.fi-chevron-down.disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.error-message {
    color: var(--danger);
    font-size: 0.75rem;
    margin-top: 0.25rem;
}
</style>
